import moment from 'moment';
import { t } from 'ttag';
import { Item, Offer, OfferTimes, TMenu } from '../contexts/site/site-context';

/**
 * Offers are what customers get without entering a code: an item for a new price, a free item, and money off a basket that is worth enough.
 * The API says what each is (amounts in pence); here it is put into words, in the customer's language, and worked out against the basket for
 * the hints that say what is missing. The API has the last word on what a basket gets, so none of this changes what is charged.
 */

export type Money = (pence: number) => string;

type Names = { reward?: string; trigger?: string };

const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

// The restaurant day starts at 03:30; before that it is still the night of the day before.
const DAY_STARTS = 330;

/** The names of the items an offer is about, as the menu has them: the same item has a row in each menu it is on, and only one is in this menu. */
export const offerNames = (offer: Offer, menu: TMenu | null | undefined): Names => {
    const items: Item[] = (menu?.categories ?? []).flatMap((category) => category.items);

    return {
        reward: items.find((item) => offer.reward_item_uids.includes(item.uid))?.name,
        trigger: items.find((item) => offer.trigger_item_uids.includes(item.uid))?.name,
    };
};

/** The category each item of the menu is in (an item is in the one category of the one menu). */
const categoriesOf = new WeakMap<TMenu, Map<string, string>>();

const categoryOf = (menu: TMenu | null | undefined, itemUid: string): string | undefined => {
    if (!menu) {
        return undefined;
    }

    let categories = categoriesOf.get(menu);

    if (!categories) {
        categories = new Map();
        menu.categories.forEach((category) => category.items.forEach((item) => categories?.set(item.uid, category.uid)));
        categoriesOf.set(menu, categories);
    }

    return categories.get(itemUid);
};

/** Whether the menu has what an offer is about: an offer for items that aren't on this menu is not for these customers. */
export const isOfferOnMenu = (offer: Offer, menu: TMenu | null | undefined): boolean => {
    if (offer.kind === 'spend_and_save' || (offer.kind === 'item_discount' && offer.scope_all)) {
        return true;
    }

    if (offer.kind === 'item_discount') {
        const categories = (menu?.categories ?? []).filter((category) => offer.scope_category_uids.includes(category.uid));
        const items = (menu?.categories ?? []).flatMap((category) => category.items).filter((item) => offer.scope_item_uids.includes(item.uid));

        return categories.length > 0 || items.length > 0;
    }

    const names = offerNames(offer, menu);

    return !!names.reward && (offer.kind !== 'buy_x_get_y' || !!names.trigger);
};

/** What an item discount is for, in words: every item, or the names of what the operator chose. */
const scopeOf = (offer: Offer): string => {
    if (offer.scope_all || offer.scope_names.length === 0) {
        return t`all items`;
    }

    const [first, second] = offer.scope_names;
    const more = offer.scope_names.length - 2;

    if (offer.scope_names.length === 1) {
        return first;
    }

    return offer.scope_names.length === 2 ? t`${first} and ${second}` : t`${first}, ${second} and ${more} more`;
};

/** What the offer says it is: the operator's own words, or ours. */
export const offerHeadline = (offer: Offer, names: Names, money: Money): string => {
    if (offer.text) {
        return offer.text;
    }

    // (what goes in a translation is a name, not a call)
    const reward = names.reward ?? t`an item`;
    const trigger = names.trigger ?? t`an item`;
    const minimum = money(offer.min_order ?? 0);
    const saving = offer.discount_type === 'percentage' ? `${offer.percent}%` : money(offer.value ?? 0);
    const percent = `${offer.percent}%`;
    const scope = scopeOf(offer);

    switch (offer.kind) {
        case 'item_discount':
            return t`${percent} off ${scope}`;

        case 'free_item':
            return offer.min_order ? t`Free ${reward} when you spend ${minimum} or more` : t`Free ${reward}`;

        case 'buy_x_get_y':
            return t`Buy ${trigger}, get ${reward} free`;

        case 'spend_and_save':
            return offer.min_order ? t`Save ${saving} when you spend ${minimum} or more` : t`Save ${saving} on your order`;
    }
};

/** A few words for the badge on an item the offer is about. */
export const offerBadge = (offer: Offer, names: Names, money: Money): string => {
    const trigger = names.trigger ?? t`an item`;
    const minimum = money(offer.min_order ?? 0);
    const percent = `${offer.percent}%`;

    switch (offer.kind) {
        case 'item_discount':
            return t`${percent} off`;

        case 'free_item':
            return offer.min_order ? t`Free when you spend ${minimum}` : t`Free`;

        case 'buy_x_get_y':
            return t`Free with ${trigger}`;

        default:
            return t`Offer`;
    }
};

/** Mon–Fri, 12:00–15:00 ... a line for each run of days that share their times. */
export const offerTimeLines = (times: OfferTimes | null): string[] => {
    if (!times) {
        return [];
    }

    const clock = (time: number) => `${String(Math.floor(time / 100) % 24).padStart(2, '0')}:${String(time % 100).padStart(2, '0')}`;
    const dayName = (day: string) => moment().isoWeekday(DAYS.indexOf(day) + 1).format('ddd');

    const groups: { days: string[]; ranges: string }[] = [];

    DAYS.forEach((day) => {
        const ranges = (times[day] ?? []).map((range) => `${clock(range.start)}–${clock(range.end)}`).join(', ');

        if (ranges === '') {
            return;
        }

        const group = groups.find((g) => g.ranges === ranges);

        if (group) {
            group.days.push(day);
        } else {
            groups.push({ days: [day], ranges });
        }
    });

    if (groups.length === 1 && groups[0].days.length === DAYS.length) {
        return [t`Every day, ${groups[0].ranges}`];
    }

    return groups.map((group) => {
        const positions = group.days.map((day) => DAYS.indexOf(day));
        const consecutive = group.days.length >= 3 && positions.every((position, i) => position === positions[0] + i);

        const days = consecutive
            ? `${dayName(group.days[0])}–${dayName(group.days[group.days.length - 1])}`
            : group.days.map(dayName).join(', ');

        return `${days}, ${group.ranges}`;
    });
};

/** The small print: how many, up to what, for how long, and when. */
export const offerDetails = (offer: Offer, money: Money): string[] => {
    const details: string[] = [];

    if (offer.kind !== 'spend_and_save' && offer.units) {
        const units = offer.units;
        details.push(t`Up to ${units} per order`);
    }

    if (offer.kind === 'spend_and_save' && offer.max_order !== null) {
        const most = money(offer.max_order);
        details.push(t`On orders up to ${most}`);
    }

    const date = (day: string) => moment(day, 'YYYY-MM-DD').format('ddd D MMM');

    if (offer.starts_on && offer.ends_on) {
        const [from, to] = [date(offer.starts_on), date(offer.ends_on)];
        details.push(t`${from} to ${to}`);
    } else if (offer.ends_on) {
        const until = date(offer.ends_on);
        details.push(t`Until ${until}`);
    } else if (offer.starts_on) {
        const from = date(offer.starts_on);
        details.push(t`From ${from}`);
    }

    return [...details, ...offerTimeLines(offer.times)];
};

/** Whether the days and times the offer works at let it be had at this moment (the API checks again, for the time the order is for). */
export const isOfferOpenAt = (offer: Offer, at: moment.Moment): boolean => {
    const day = at.format('YYYY-MM-DD');

    if ((offer.starts_on && day < offer.starts_on) || (offer.ends_on && day > offer.ends_on)) {
        return false;
    }

    if (!offer.times) {
        return true;
    }

    let time = at.hours() * 100 + at.minutes();
    let weekday = at.isoWeekday() - 1;

    // half past midnight is 24:30 of the night before
    if (time < DAY_STARTS) {
        time += 2400;
        weekday = (weekday + 6) % 7;
    }

    return (offer.times[DAYS[weekday]] ?? []).some((range) => time >= range.start && time <= range.end);
};

/** The offers that are about an item: it is reduced (an item discount, for it or its category or every item), or it is the free one. */
export const offersForItem = (offers: Offer[], itemUid: string, menu: TMenu | null | undefined): Offer[] =>
    offers.filter((offer) => {
        switch (offer.kind) {
            case 'spend_and_save':
                return false;

            case 'item_discount':
                return (
                    !!offer.scope_all ||
                    offer.scope_item_uids.includes(itemUid) ||
                    offer.scope_category_uids.includes(categoryOf(menu, itemUid) ?? '')
                );

            default:
                return offer.reward_item_uids.includes(itemUid);
        }
    });

/** The price an item costs with its offer, when it has one that changes it: an item discount's percentage off, the best of them. */
export const offerPriceOf = (offers: Offer[], item: Item): number | null => {
    const prices = offers
        .filter((offer) => offer.kind === 'item_discount' && !!offer.percent && offer.percent > 0 && offer.percent < 100)
        .map((offer) => item.price - Math.round(item.price * (offer.percent as number) * 0.01));

    return prices.length ? Math.min(...prices) : null;
};

type Line = { uid: string; quantity?: number; price: number; modifiersTotal?: number };

const lineTotal = (line: Line): number => (line.price + (line.modifiersTotal ?? 0)) * (line.quantity ?? 1);

/**
 * What is missing from the basket for an offer, in words: the thing to do to get it, with how far off it is for choosing which to say. Nothing when
 * the offer has come to the customer (or is for something that isn't going to happen).
 */
const hintFor = (offer: Offer, lines: Line[], names: Names, money: Money): { text: string; gap: number } | null => {
    const spent = (excluding: string[]) => lines.filter((line) => !excluding.includes(line.uid)).reduce((total, line) => total + lineTotal(line), 0);

    if (offer.kind === 'spend_and_save') {
        const subtotal = spent([]);

        if (!offer.min_order || subtotal >= offer.min_order || subtotal === 0) {
            return null;
        }

        const more = money(offer.min_order - subtotal);
        const saving = offer.discount_type === 'percentage' ? `${offer.percent}%` : money(offer.value ?? 0);

        return { text: t`Spend ${more} more to save ${saving}`, gap: offer.min_order - subtotal };
    }

    if ((offer.kind !== 'free_item' && offer.kind !== 'buy_x_get_y') || !names.reward) {
        return null;
    }

    const reward = names.reward;
    const hasReward = lines.some((line) => offer.reward_item_uids.includes(line.uid));

    if (offer.kind === 'buy_x_get_y') {
        const trigger = names.trigger ?? t`an item`;
        const hasTrigger = lines.some((line) => offer.trigger_item_uids.includes(line.uid));

        if (hasTrigger && !hasReward) {
            return { text: t`Add ${reward} to your order, it’s free with your ${trigger}`, gap: 0 };
        }

        // (the item customers buy is the free one too, and there is only the one they are buying)
        if (hasTrigger && hasReward && discountOf(offer, null, lines, 0) === 0) {
            return { text: t`Add another ${reward} to your order, it’s free with your ${trigger}`, gap: 0 };
        }

        // (far from what customers have in the basket, so a hint about spending says more)
        return !hasTrigger && lines.length > 0 ? { text: t`Buy ${trigger} and get ${reward} free`, gap: 1000000 } : null;
    }

    const spend = spent(offer.reward_item_uids);

    if (spend === 0) {
        return null;
    }

    if (offer.min_order && spend < offer.min_order) {
        const more = money(offer.min_order - spend);

        return { text: t`Spend ${more} more to get ${reward} free`, gap: offer.min_order - spend };
    }

    return hasReward ? null : { text: t`Add ${reward} to your order, it’s free`, gap: 0 };
};

/**
 * The one thing to say to a customer about an offer they are close to: the one that needs least. Offers that aren't open at the time of the
 * order, or are for an item this menu hasn't got, aren't mentioned.
 */
export const basketHint = (offers: Offer[], menu: TMenu | null | undefined, lines: Line[], at: moment.Moment, money: Money): string | null => {
    const hints = offers
        .filter((offer) => isOfferOnMenu(offer, menu) && isOfferOpenAt(offer, at))
        .map((offer) => hintFor(offer, lines, offerNames(offer, menu), money))
        .filter((hint): hint is { text: string; gap: number } => hint !== null)
        .sort((a, b) => a.gap - b.gap);

    return hints.length ? hints[0].text : null;
};

/** The time at the site now, whatever the time on the customer's device (offers run on the site's clock). */
export const siteNow = (timezone?: string | null): moment.Moment => {
    try {
        if (timezone) {
            return moment(new Date().toLocaleString('sv-SE', { timeZone: timezone }), 'YYYY-MM-DD HH:mm:ss');
        }
    } catch (e) {
        // (a timezone the browser doesn't know: the customer's own clock will do)
    }

    return moment();
};

/**
 * What a discount on the basket or the order is called: for an offer, what it is in words (when the menu has the names of its items to put
 * in them); otherwise what the API called it.
 */
export const discountLabel = (code: { text: string; offer?: Offer }, menu: TMenu | null | undefined, money: Money): string => {
    const offer = code.offer;

    if (!offer) {
        return code.text;
    }

    const names = offerNames(offer, menu);

    if (!offer.text && (offer.kind === 'free_item' || offer.kind === 'buy_x_get_y') && (!names.reward || (offer.kind === 'buy_x_get_y' && !names.trigger))) {
        return code.text || t`Offer`;
    }

    return offerHeadline(offer, names, money);
};

/**
 * What an offer comes to for a basket, worked out here for showing the customer while they order (the API works it out again, and has the last
 * word, when the basket is validated). It follows the API: the best offer the basket qualifies for, one at a time.
 */
export type OfferEstimate = { offer: Offer; discount: number };

type EstimateLine = { uid: string; price: number; quantity?: number; modifiersTotal?: number };

/** What is saved on the units of the lines an offer is for: for no more than `cap` units in all, the saving on each, line by line (not on the `kept` units of a line). */
const unitSavings = (
    lines: EstimateLine[],
    isFor: (line: EstimateLine) => boolean,
    cap: number | null,
    saving: (line: EstimateLine) => number,
    kept: (line: EstimateLine) => number = () => 0
): number => {
    let remaining = cap ?? Number.MAX_SAFE_INTEGER;
    let total = 0;

    lines.forEach((line) => {
        if (remaining <= 0 || !isFor(line)) {
            return;
        }

        const units = Math.min((line.quantity ?? 1) - kept(line), remaining);
        const each = Math.max(0, saving(line));

        if (units > 0 && each > 0) {
            total += units * each;
            remaining -= units;
        }
    });

    return total;
};

const discountOf = (offer: Offer, menu: TMenu | null | undefined, lines: EstimateLine[], extras: number): number => {
    const subtotal = lines.reduce((total, line) => total + lineTotal(line), 0);

    switch (offer.kind) {
        case 'item_discount': {
            const percent = offer.percent ?? 0;

            if (percent <= 0 || percent >= 100) {
                return 0;
            }

            return unitSavings(
                lines,
                (line) =>
                    !!offer.scope_all ||
                    offer.scope_item_uids.includes(line.uid) ||
                    offer.scope_category_uids.includes(categoryOf(menu, line.uid) ?? ''),
                offer.units,
                (line) => Math.round((line.price + Math.max(0, line.modifiersTotal ?? 0)) * percent * 0.01)
            );
        }

        case 'free_item':
        case 'buy_x_get_y': {
            const rewards = offer.reward_item_uids;
            const isReward = (line: EstimateLine) => rewards.includes(line.uid);

            if (rewards.length === 0) {
                return 0;
            }

            const unlocked =
                offer.kind === 'buy_x_get_y'
                    ? lines.some((line) => offer.trigger_item_uids.includes(line.uid) && (line.quantity ?? 1) > 0)
                    : (() => {
                          const spend = lines.filter((line) => !isReward(line)).reduce((total, line) => total + lineTotal(line), 0);

                          return spend > 0 && spend >= (offer.min_order ?? 0);
                      })();

            // (what is added to the free item is free as well, when the offer says so)
            const saving = (line: EstimateLine) => line.price + (offer.free_modifiers ? Math.max(0, line.modifiersTotal ?? 0) : 0);

            // (what customers buy can be the free item itself, but a free unit isn't what was bought: one unit of what unlocks the offer is left to pay,
            // when nothing else in the basket unlocks it)
            const isTrigger = (line: EstimateLine) => offer.trigger_item_uids.includes(line.uid) && (line.quantity ?? 1) > 0;
            let toPay = offer.kind === 'buy_x_get_y' && !lines.some((line) => isTrigger(line) && !isReward(line)) ? 1 : 0;

            const kept = (line: EstimateLine) => {
                if (toPay <= 0 || !isTrigger(line)) {
                    return 0;
                }

                const units = Math.min(toPay, line.quantity ?? 1);
                toPay -= units;

                return units;
            };

            return unlocked ? unitSavings(lines, isReward, Math.max(1, offer.units ?? 1), saving, kept) : 0;
        }

        case 'spend_and_save': {
            if (subtotal <= 0 || (offer.min_order && subtotal < offer.min_order) || (offer.max_order !== null && subtotal > offer.max_order)) {
                return 0;
            }

            const base = offer.applies_to === 'total' ? subtotal + extras : subtotal;
            const discount = offer.discount_type === 'percentage' ? Math.round(base * (offer.percent ?? 0) * 0.01) : Math.min(offer.value ?? 0, base);

            return Math.max(0, Math.min(discount, base, subtotal));
        }
    }

    return 0;
};

/**
 * The best offer a basket gets, if it gets one: of the offers on the menu that are open at the time given, the one that takes most off (the first of equals).
 *
 * @param extras what the delivery fee comes to, for an offer that takes a percentage of the total
 * @param kinds only these kinds of offer, for when it is one item that is being looked at
 */
export const estimateOffer = (
    offers: Offer[],
    menu: TMenu | null | undefined,
    lines: EstimateLine[],
    extras: number,
    at: moment.Moment,
    kinds?: Offer['kind'][]
): OfferEstimate | null => {
    let best: OfferEstimate | null = null;

    offers
        .filter((offer) => (!kinds || kinds.includes(offer.kind)) && isOfferOnMenu(offer, menu) && isOfferOpenAt(offer, at))
        .forEach((offer) => {
            const discount = discountOf(offer, menu, lines, extras);

            if (discount > 0 && (!best || discount > best.discount)) {
                best = { offer, discount };
            }
        });

    return best;
};
